import { useParams, useNavigate } from 'react-router-dom'
import { useUsers, useUserSuggestions } from '@/hooks/useUsers'
import { Button } from '@brent/core-ui/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { CreateUserSuggestionDialog } from '@/components/features/suggestions/CreateUserSuggestionDialog'
import { EditUserSuggestionDialog } from '@/components/features/suggestions/EditUserSuggestionDialog'
import { DeleteUserSuggestionDialog } from '@/components/features/suggestions/DeleteUserSuggestionDialog'

export function MemberProfile() {
  const { username } = useParams<{ username: string }>()
  const navigate = useNavigate()
  const { data: users, isLoading: usersLoading, error: usersError } = useUsers()
  const {
    data: suggestions,
    isLoading: suggestionsLoading,
    error: suggestionsError,
  } = useUserSuggestions(username)

  // Find the specific user
  const user = users?.find((u) => u.username === username)

  if (usersLoading || suggestionsLoading) {
    return <div className="h-96 animate-pulse rounded-lg bg-muted" />
  }

  if (usersError || suggestionsError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load member profile.{' '}
          {usersError instanceof Error
            ? usersError.message
            : suggestionsError instanceof Error
              ? suggestionsError.message
              : 'Unknown error'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Members
        </Button>
        <Alert variant="destructive">
          <AlertDescription>Member not found.</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/users')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Members
      </Button>

      {/* Member Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{user.name || 'Unknown'}</CardTitle>
              <CardDescription>{user.email}</CardDescription>
            </div>
            <Badge variant={user.enabled ? 'default' : 'secondary'}>{user.status}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <span className="text-sm font-medium">Username:</span>
              <p className="text-sm text-muted-foreground">{user.username}</p>
            </div>
            <div>
              <span className="text-sm font-medium">Instrument:</span>
              <p className="text-sm text-muted-foreground">{user.instrument || 'Not specified'}</p>
            </div>
            <div>
              <span className="text-sm font-medium">Account Created:</span>
              <p className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium">Status:</span>
              <p className="text-sm text-muted-foreground">
                {user.enabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suggestions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Suggestions ({suggestions?.length || 0})</CardTitle>
              <CardDescription>Pieces suggested by {user.name || user.email}</CardDescription>
            </div>
            <CreateUserSuggestionDialog username={username!} memberName={user.name || user.email} />
          </div>
        </CardHeader>
        <CardContent>
          {!suggestions || suggestions.length === 0 ? (
            <Alert>
              <AlertDescription>This member has not made any suggestions yet.</AlertDescription>
            </Alert>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Composer</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="max-w-md">Comment</TableHead>
                    <TableHead>Suggested</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.map((suggestion) => (
                    <TableRow key={suggestion.suggestion_id}>
                      <TableCell className="font-medium">
                        {suggestion.composer_wikidata_id ? (
                          <a
                            href={`https://www.wikidata.org/wiki/${suggestion.composer_wikidata_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {suggestion.composer}
                          </a>
                        ) : (
                          suggestion.composer
                        )}
                      </TableCell>
                      <TableCell>
                        {suggestion.work_wikidata_id ? (
                          <a
                            href={`https://www.wikidata.org/wiki/${suggestion.work_wikidata_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {suggestion.title}
                          </a>
                        ) : (
                          suggestion.title
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        {suggestion.comment ? (
                          <span className="text-sm text-muted-foreground">
                            {suggestion.comment}
                          </span>
                        ) : (
                          <span className="text-sm italic text-muted-foreground">No comment</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <EditUserSuggestionDialog
                            username={username!}
                            suggestion={suggestion}
                            memberName={user.name || user.email}
                          />
                          <DeleteUserSuggestionDialog
                            username={username!}
                            suggestion={suggestion}
                            memberName={user.name || user.email}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
